<script setup lang="ts">
import type { TpgClickEvent } from '@tpg/grammar-web';
import type { Slot } from 'vue';
import { computed } from 'vue';

export interface GrammarCardProps {
  selected?: boolean;
  noBackground?: boolean;
  readOnly?: boolean;
  primaryLabel?: string;
  secondaryLabel?: string;
  heading?: string;
  subtitle?: string;
  currentIndicator?: boolean;
}

interface GrammarCardSlots {
  'header-leading-buttons'?: Slot;
  'header-stuff'?: Slot;
  'header-trailing-buttons'?: Slot;
  'card-row-1'?: Slot;
  'card-row-2'?: Slot;
}

const props = withDefaults(defineProps<GrammarCardProps>(), {
  selected: false,
  noBackground: false,
  readOnly: false,
  primaryLabel: '',
  secondaryLabel: '',
  heading: '',
  subtitle: '',
  currentIndicator: false,
});

const emit = defineEmits<{
  (event: 'click', data: TpgClickEvent): void;
}>();

const slots = defineSlots<GrammarCardSlots>();

function onClick(event: TpgClickEvent) {
  emit('click', event);
}

const truncatedPrimaryLabel = computed(() => {
  const value = props.primaryLabel ?? '';
  return value.length > 35 ? `${value.slice(0, 35)}...` : value;
});

const webComponentProps = computed(() => ({
  ...props,
  primaryLabel: truncatedPrimaryLabel.value,
}));
</script>

<template>
  <tpg-card v-bind="webComponentProps" @tpg-click="onClick">
    <div
      v-if="slots['header-leading-buttons']"
      slot="header-leading-buttons"
      class="inline-flex"
    >
      <slot name="header-leading-buttons" />
    </div>

    <div v-if="slots['header-stuff']" slot="header-stuff">
      <slot name="header-stuff" />
    </div>

    <div
      v-if="slots['header-trailing-buttons']"
      slot="header-trailing-buttons"
      class="inline-flex"
    >
      <slot name="header-trailing-buttons" />
    </div>

    <div v-if="slots['card-row-1']" slot="card-row-1">
      <slot name="card-row-1" />
    </div>

    <div v-if="slots['card-row-2']" slot="card-row-2">
      <slot name="card-row-2" />
    </div>
  </tpg-card>
</template>
